import { useCallback, useSyncExternalStore } from 'react';

/**
 * The two peer grounds. Not a light and a dark mode — DESIGN.md is explicit that
 * neither is a recolour of the other, and the hub carries both as first-class.
 */
export type Ground = 'paper' | 'navy';

/**
 * The key the public site writes, deliberately shared: a member who picked navy
 * on natshoot.co.za lands in the hub on navy. Changing it here silently breaks
 * that continuity, so it is the one string both surfaces must agree on.
 */
const KEY = 'ns-ground';

const isGround = (value: string | null): value is Ground => value === 'paper' || value === 'navy';

/** localStorage throws in private mode on some browsers; a lost preference is not an error. */
const readStored = (): Ground | null => {
  try {
    const saved = localStorage.getItem(KEY);
    return isGround(saved) ? saved : null;
  } catch {
    return null;
  }
};

const readAttribute = (): Ground | null => {
  if (typeof document === 'undefined') return null;
  const current = document.documentElement.getAttribute('data-ground');
  return isGround(current) ? current : null;
};

/**
 * The ground that should be live, in priority order: whatever is already
 * stamped on `<html>`, then an explicit stored choice, then paper.
 *
 * **Paper is the default and nothing else can displace it** — team decision,
 * 2026-08-09. Two inputs were removed to make that true: `prefers-color-scheme`,
 * which used to hand a dark-system member navy, and (in the host's head script)
 * the seed from the portal's own `darkMode` preference. Only a member's own
 * choice moves the hub off paper now.
 *
 * The host's `index.html` runs this same resolution before first paint. What
 * follows is the module's standalone fallback — it is what keeps the module
 * liftable, since a project that drops it in without adding the head script
 * still resolves a correct ground, just one frame later.
 */
const resolveGround = (): Ground => readAttribute() ?? readStored() ?? 'paper';

/* ---------------------------------------------------------------------------
   A module-level store, rather than per-hook state.

   Several components read the ground at once — the switch itself, and the logo,
   which has to pick the navy or the white lockup now that `dark:` no longer
   applies to it. With `useState` in each caller, pressing the switch would
   update only the switch's own copy and the logo would keep the stale mark. The
   attribute on <html> is the real source of truth; this store is the
   notification channel that lets React see it change.
--------------------------------------------------------------------------- */

let current: Ground = typeof document === 'undefined' ? 'paper' : resolveGround();
const listeners = new Set<() => void>();

const emit = (): void => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = (): Ground => current;

/** Writes the attribute — the thing every ground token actually hangs off — then notifies. */
const applyGround = (next: Ground, persist: boolean): void => {
  current = next;
  document.documentElement.setAttribute('data-ground', next);
  if (persist) {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* private mode — the choice just won't survive the session */
    }
  }
  emit();
};

// If the head script never ran — the module lifted into a project without it —
// stamp the attribute now so the tokens resolve. Not persisted: nothing has been
// chosen yet, and writing here would freeze a guess as a preference.
if (typeof document !== 'undefined' && !readAttribute()) {
  applyGround(current, false);
}

// The hub used to follow `prefers-color-scheme` for members who had not chosen, so a
// system flip would swap the ground live. That listener is gone with the default: paper
// is the ground until a member says otherwise, and the system no longer has a vote.

/** Reads and sets the live ground. */
export const useGround = (): { ground: Ground; setGround: (next: Ground) => void } => {
  const ground = useSyncExternalStore(subscribe, getSnapshot, () => 'paper' as Ground);
  const setGround = useCallback((next: Ground): void => applyGround(next, true), []);
  return { ground, setGround };
};
